import { supabase } from '@/lib/supabaseClient';
import { isAssigneeRecord, toUserRecord } from '@/lib/assigneeName';
import { getUserDisplayName } from '@/utils/userUtils';
import type { SlaAge } from '@/lib/stageCollapse';

export interface PipelineFilterOptions {
  /**
   * SUB-4.5 · FR41. WHOSE BOARD THIS IS.
   *
   * The pipeline board is drawn one account at a time, and in merged scope the
   * account it draws is the one owning the selected pipeline, which is NOT the
   * session's account: merged scope anchors at the agency, so
   * `get_current_user_tenant_id()` answers "the agency" while the open tab is a
   * client's. Left unset, every query below stays pinned to the session account
   * exactly as it was before this story.
   *
   * ⚠️ THIS IS A NARROWING WITHIN AN ALREADY-BOUNDED SET, NEVER THE BOUND ITSELF
   * (ISO4). `projects_select` has resolved through `merged_scope_tenant_ids()`
   * since `20260813s`, so an id from outside the caller's subtree returns zero
   * rows rather than widening anything. Same doctrine as
   * `accountAttribution.ts`: the server performs the narrowing, so it cannot be
   * mistaken for the bound.
   */
  accountTenantId?: string | null;
  search?: string;
  statusFilter?: string | null;
  teamId?: number | null;
  assignedRepId?: string | null;
  dateRange?: { from: Date | null; to: Date | null } | null;
  stageChangeDateRange?: { from: Date | null; to: Date | null } | null;
  hasOpenComments?: boolean;
  systemSizeRange?: [number, number] | null;
}

export interface StageAggregates {
  totalValue: number;
  totalKW: number;
  /** Every project in the stage, not just the loaded page, so a collapsed column can show SLA health. */
  slaAges?: SlaAge[];
}

class PipelinePaginationService {
  /**
   * SUB-4.2. The resolved account, memoised.
   *
   * ⚠️ IT USED TO BE MEMOISED FOR THE LIFETIME OF THE MODULE, and
   * `get_current_user_tenant_id()` is VOLATILE for a reason: it revalidates the
   * session row on every call, which is what makes ISO13 / FR66 immediate rather
   * than eventual. Caching its answer until the page reloaded threw that away, so
   * an account switch or a scope change left every query here pinned to the
   * previous account until the tab was refreshed.
   *
   * Three things now stop that, deliberately overlapping, because the way this
   * class of bug survives is one call site nobody updated:
   *   1. a short TTL, so a change nobody announced self-heals within seconds;
   *   2. `getTenantId` COMPARES the fresh answer to the cached one and clears the
   *      row caches itself when it moved (the outcome, not the notification);
   *   3. every row-cache key carries the account, so a stale entry from another
   *      account cannot be found by a lookup made in this one.
   */
  private tenantIdCache: string | null = null;
  private tenantIdCachedAt: number = 0;
  private readonly tenantIdCacheTtl: number = 15 * 1000;
  private userDataCache: Map<string, any> = new Map();
  private cacheExpiry: number = 5 * 60 * 1000; // 5 minutes
  private lastCacheUpdate: number = 0;
  
  // Project caching for faster search
  private projectCache: Map<string, any[]> = new Map(); // key: pipelineId-stageId, value: all projects
  private searchCache: Map<string, any> = new Map(); // key: search+filters hash, value: search results
  private projectCacheExpiry: number = 2 * 60 * 1000; // 2 minutes
  private lastProjectCacheUpdate: Map<string, number> = new Map();
  private fetchingPromises: Map<string, Promise<any[]>> = new Map(); // Prevent duplicate fetches

  // Clear project cache - useful when projects are updated (like retention status changes)
  clearProjectCache(): void {
    this.projectCache.clear();
    this.searchCache.clear();
    this.lastProjectCacheUpdate.clear();
    this.fetchingPromises.clear();
  }

  /**
   * SUB-4.2. Forget everything that depended on which account we were in.
   *
   * Called by `TenantContext` on an account switch and on entering or leaving
   * merged scope. It is the announcement half; `getTenantId` below is the half
   * that works when nobody announces.
   *
   * SUB-4.7 · AC2. Now called from `TenantContext.dropScopedCaches()`, which is
   * the single sweep all four announce paths take, and which ALSO runs when the
   * reach moves with no client action at all: a revoked grant, an archived
   * anchor, a detached sub-account, or a second tab of the same user. That last
   * case is the one no announce path covers, because nobody announces it.
   *
   * ⚠️ The argument for one sweep is one paragraph above this: `returnToHomeTenant`
   * cleared NOTHING until SUB-4.2 noticed. A list of clears repeated at four call
   * sites is a list one of them will be missing from.
   */
  resetScopeCaches(): void {
    this.tenantIdCache = null;
    this.tenantIdCachedAt = 0;
    this.clearProjectCache();
  }

  // Clear cache for specific pipeline
  clearPipelineCache(pipelineId: number): void {
    const keysToDelete: string[] = [];
    this.projectCache.forEach((_, key) => {
      // ⚠️ Keys are `tenantId|pipelineId|stageId`, so this matches the pipeline
      // SEGMENT. It used to be a `startsWith` on the pipeline id, which stopped
      // matching the moment the account was prepended, and a cache clear that
      // silently matches nothing is worse than no cache clear at all.
      if (key.split('|')[1] === String(pipelineId)) {
        keysToDelete.push(key);
      }
    });
    keysToDelete.forEach(key => {
      this.projectCache.delete(key);
      this.lastProjectCacheUpdate.delete(key);
    });

    // Clear search cache as well since it may contain stale data
    this.searchCache.clear();
  }

  // Cache tenant ID to avoid repeated RPC calls, for a few seconds at a time.
  private async getTenantId(): Promise<string> {
    const now = Date.now();
    if (this.tenantIdCache && now - this.tenantIdCachedAt < this.tenantIdCacheTtl) {
      return this.tenantIdCache;
    }

    const { data: tenantId, error: tenantError } = await supabase.rpc('get_current_user_tenant_id');
    if (tenantError) throw tenantError;

    // ⚠️ THE ANSWER MOVING IS THE EVENT. Nothing has to tell this service that the
    // account changed: it finds out by asking, and drops the rows it fetched under
    // the old one. Waiting to be told is how the previous version stayed wrong.
    if (this.tenantIdCache && this.tenantIdCache !== tenantId) {
      this.clearProjectCache();
    }

    this.tenantIdCache = tenantId;
    this.tenantIdCachedAt = now;
    return tenantId;
  }

  /**
   * SUB-4.5. The account THIS query is about.
   *
   * ⚠️ `getTenantId()` IS STILL CALLED EVEN WHEN THE CALLER NAMES AN ACCOUNT,
   * and the call is the point rather than the answer. That method is where "the
   * answer moving is the event" lives: it compares the fresh session account to
   * the cached one and drops the row caches itself when it moved. Short-
   * circuiting it for board reads would disable the only detector that works
   * when nobody announces the change, which is the case SUB-4.2 wrote it for.
   * The RPC behind it is memoised for 15 seconds, so the call is nearly free.
   */
  private async resolveAccount(filters: PipelineFilterOptions): Promise<string> {
    const sessionTenantId = await this.getTenantId();
    return filters.accountTenantId || sessionTenantId;
  }

  /**
   * A row-cache key that carries the account it was fetched under.
   *
   * `pipeline_id` belongs to exactly one account, so this looks redundant.
   *
   * ⚠️ SUB-4.5 UPDATE. It is not redundant, and the reason moved. SUB-4.2
   * predicted that this story would widen `pipelines_select`; it did not (D31,
   * `20260814a`). What it did instead is let the board name the account it is
   * drawing, so the SAME pipeline id is now fetched under the agency account in
   * single scope and under the client account on a merged tab. A key naming only
   * the pipeline and the stage would be a key those two reads share.
   */
  private stageKey(tenantId: string, pipelineId: number, stageId: number): string {
    return `${tenantId}|${pipelineId}|${stageId}`;
  }

  /**
   * SUB-4.16 · F-50's twin on `users`. The people this board needs to NAME.
   *
   * ⚠️ **`merged_scope_user_names()`, NOT `.from('users')`.** `users_select` is
   * `tenant_id = get_current_user_tenant_id()`, so a direct read returns nothing
   * for a sub-account's staff and every card on a merged board fell back to
   * printing the rep's raw uuid. The RPC carries all three caller-facing arms of
   * that policy with the merged resolver swapped in (D69), so it is EXACTLY the
   * policy in single-account scope and widens only across the subtree.
   *
   * ⚠️ **CALLED ON EVERY BOARD READ, MERGED OR NOT.** Same one-code-path choice
   * D67 made for stages: branching on scope leaves two readings of one name.
   *
   * ⚠️ **THE ERROR IS RETURNED, NOT SWALLOWED.** This used to be
   * `const { data: userData } = await supabase...` with no `error` binding, so a
   * failed fetch left the cache empty and the card said "ID: <uuid>" with
   * nothing logged anywhere. The caller needs to tell "nobody is assigned" from
   * "we could not read who", and it cannot do that from an empty map.
   */
  private async getCachedUserData(
    userIds: string[],
  ): Promise<{ byId: Record<string, any>; failed: boolean }> {
    const now = Date.now();
    const uncachedIds = userIds.filter(id =>
      !this.userDataCache.has(id) ||
      (now - this.lastCacheUpdate) > this.cacheExpiry
    );

    let failed = false;

    if (uncachedIds.length > 0) {
      const { data: userData, error } = await supabase.rpc('merged_scope_user_names', {
        p_user_ids: uncachedIds,
      });

      if (error) {
        console.error('Error fetching assignee names:', error);
        failed = true;
      } else {
        (Array.isArray(userData) ? userData : []).filter(isAssigneeRecord).forEach(user => {
          this.userDataCache.set(user.user_id, {
            id: user.user_id,
            // `getUserDisplayName` stays the one definition of a display name,
            // suffix and deleted-placeholder rules included.
            name: getUserDisplayName(toUserRecord(user)).trim(),
            email: user.email,
            avatarUrl: user.avatar_url || '',
            record: user,
          });
        });

        this.lastCacheUpdate = now;
      }
    }

    const byId: Record<string, any> = {};
    userIds.forEach(id => {
      if (this.userDataCache.has(id)) {
        byId[id] = this.userDataCache.get(id);
      }
    });
    return { byId, failed };
  }

  // Create cache key for search results
  private createCacheKey(pipelineId: number, filters: PipelineFilterOptions): string {
    // Use a simpler string concatenation instead of JSON.stringify + btoa for better performance
    const parts = [
      pipelineId,
      filters.search || '',
      filters.statusFilter || '',
      filters.teamId || '',
      filters.assignedRepId || '',
      filters.dateRange ? `${filters.dateRange.from}-${filters.dateRange.to}` : '',
      filters.stageChangeDateRange ? `${filters.stageChangeDateRange.from}-${filters.stageChangeDateRange.to}` : '',
      filters.hasOpenComments ? '1' : '0'
    ];
    return parts.join('|');
  }

  // Check if we have cached data for all projects in a stage
  private hasCachedAllProjects(key: string): boolean {
    const lastUpdate = this.lastProjectCacheUpdate.get(key);
    if (!lastUpdate) return false;

    const now = Date.now();
    return (now - lastUpdate) < this.projectCacheExpiry && this.projectCache.has(key);
  }

  // Get cached projects or fetch all if not cached
  private async getAllStageProjects(pipelineId: number, stageId: number, filters: PipelineFilterOptions): Promise<any[]> {
    // ⚠️ RESOLVED FIRST, AND THAT ORDER MATTERS. This call is what notices an
    // account change and clears the row caches, so it has to happen BEFORE the key
    // is built and looked up. Building the key from a stale id and then refreshing
    // it would read the previous account's rows one last time.
    const tenantId = await this.resolveAccount(filters);
    const key = this.stageKey(tenantId, pipelineId, stageId);

    // Don't use cache when filters are active - ensures search results are fresh
    const hasActiveFilters = filters.search || filters.statusFilter || filters.teamId ||
                            filters.assignedRepId || filters.dateRange ||
                            filters.stageChangeDateRange || filters.systemSizeRange ||
                            filters.hasOpenComments;

    // If we have cached data and no filters, use it
    if (!hasActiveFilters && this.hasCachedAllProjects(key)) {
      const cachedProjects = this.projectCache.get(key) || [];
      return cachedProjects;
    }

    // If we're already fetching this stage, return the existing promise
    if (this.fetchingPromises.has(key)) {
      return await this.fetchingPromises.get(key)!;
    }

    // Create fetch promise and cache it to prevent duplicates
    const fetchPromise = this.fetchAllProjectsForStage(tenantId, pipelineId, stageId).then(projects => {
      // Only cache if no filters are active
      if (!hasActiveFilters) {
        this.projectCache.set(key, projects);
        this.lastProjectCacheUpdate.set(key, Date.now());
      }
      this.fetchingPromises.delete(key); // Clean up promise cache
      return projects;
    }).catch(error => {
      this.fetchingPromises.delete(key); // Clean up on error too
      throw error;
    });

    this.fetchingPromises.set(key, fetchPromise);
    return await fetchPromise;
  }

  // Fetch all projects for a stage (without filters)
  //
  // The account is passed in rather than resolved again, so the rows and the cache
  // key they are stored under cannot come from two different answers.
  private async fetchAllProjectsForStage(tenantId: string, pipelineId: number, stageId: number): Promise<any[]> {
    const { data: projectsData, error } = await supabase
      .from('projects')
      .select(`
        id,
        project_name,
        company_name,
        system_cost,
        total_project_profit,
        estimated_system_size,
        project_city,
        project_state,
        assigned_sales_rep_id,
        assigned_internal_pm_id,
        current_stage_id,
        created_at,
        updated_at,
        stage_changed_at,
        project_status,
        project_type,
        commercial_type,
        pipeline_id,
        tenant_id,
        created_by_user,
        first_name,
        last_name,
        contact_email,
        contact_phone,
        utility_provider,
        project_full_address,
        company_street,
        company_city,
        company_state,
        property_owner_name,
        is_retention,
        assigned_proposal_designer_id
      `)
      .eq('pipeline_id', pipelineId)
      .eq('current_stage_id', stageId)
      .eq('tenant_id', tenantId)
      .neq('project_status', 'Deleted')
      .is('deleted_at', null)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return projectsData || [];
  }

  // Apply filters to a Supabase query
  private applyFilters(query: any, filters: PipelineFilterOptions) {
    if (filters.search) {
      const searchTerm = filters.search.toLowerCase();
      // Note: Cannot search nested relations (assigned_sales_rep.first_name) in .or() filter
      // Sales rep search is handled client-side after data is loaded with user profiles
      query = query.or(`project_name.ilike.%${searchTerm}%,company_name.ilike.%${searchTerm}%,project_city.ilike.%${searchTerm}%,project_state.ilike.%${searchTerm}%,project_street.ilike.%${searchTerm}%,first_name.ilike.%${searchTerm}%,last_name.ilike.%${searchTerm}%,contact_email.ilike.%${searchTerm}%,contact_phone.ilike.%${searchTerm}%,utility_provider.ilike.%${searchTerm}%,project_full_address.ilike.%${searchTerm}%,company_street.ilike.%${searchTerm}%,company_city.ilike.%${searchTerm}%,company_state.ilike.%${searchTerm}%,property_owner_name.ilike.%${searchTerm}%`);
    }

    if (filters.statusFilter) {
      query = query.eq('project_status', filters.statusFilter);
    }

    // Note: Team filtering is handled via user relationships in the application layer
    // since it requires joining with user_teams table

    if (filters.dateRange?.from) {
      query = query.gte('created_at', filters.dateRange.from.toISOString());
    }
    if (filters.dateRange?.to) {
      query = query.lte('created_at', filters.dateRange.to.toISOString());
    }

    if (filters.stageChangeDateRange?.from) {
      query = query.gte('stage_changed_at', filters.stageChangeDateRange.from.toISOString());
    }
    if (filters.stageChangeDateRange?.to) {
      query = query.lte('stage_changed_at', filters.stageChangeDateRange.to.toISOString());
    }

    if (filters.systemSizeRange) {
      query = query.gte('estimated_system_size', filters.systemSizeRange[0]);
      query = query.lte('estimated_system_size', filters.systemSizeRange[1]);
    }

    return query;
  }

  // Get count of projects for a specific stage with filters
  async getStageCount(pipelineId: number, stageId: number, filters: PipelineFilterOptions): Promise<number> {
    try {
      const tenantId = await this.resolveAccount(filters);

      // For team filtering or hasOpenComments, use client-side filtering
      if (filters.teamId || filters.hasOpenComments) {
        const allProjects = await this.getAllStageProjects(pipelineId, stageId, filters);

        // Process projects with user data FIRST, so we have team names for filtering
        const processedProjects = await this.processProjectsWithUserData(allProjects);

        // THEN filter and count
        const filteredProjects = this.filterProjectsClientSide(processedProjects, filters);
        return filteredProjects.length;
      }

      // Use optimized database function for search and other filters
      if (filters.search || filters.statusFilter || filters.assignedRepId ||
          filters.dateRange || filters.stageChangeDateRange || filters.systemSizeRange) {

        const { data: count, error } = await supabase.rpc('count_projects_optimized', {
          p_pipeline_id: pipelineId,
          p_stage_id: stageId,
          p_tenant_id: tenantId,
          p_search_term: filters.search || null,
          p_status_filter: filters.statusFilter || null,
          p_assigned_rep_id: filters.assignedRepId || null,
          p_date_from: filters.dateRange?.from?.toISOString() || null,
          p_date_to: filters.dateRange?.to?.toISOString() || null,
          p_stage_date_from: filters.stageChangeDateRange?.from?.toISOString() || null,
          p_stage_date_to: filters.stageChangeDateRange?.to?.toISOString() || null,
          p_system_size_min: filters.systemSizeRange?.[0] || null,
          p_system_size_max: filters.systemSizeRange?.[1] || null
        });

        if (error) throw error;
        return count || 0;
      }

      // For non-filtered queries, use simple server-side count
      const query = supabase
        .from('projects')
        .select('*', { count: 'exact', head: true })
        .eq('pipeline_id', pipelineId)
        .eq('current_stage_id', stageId)
        .eq('tenant_id', tenantId)
        .neq('project_status', 'Deleted')
        .is('deleted_at', null);

      const { count, error } = await query;
      if (error) throw error;

      return count || 0;
    } catch (error) {
      console.error('Error getting stage count:', error);
      return 0;
    }
  }

  // Get counts for all stages
  async getAllStageCounts(pipelineId: number, stageIds: number[], filters: PipelineFilterOptions): Promise<Record<number, number>> {
    const counts: Record<number, number> = {};
    
    // Fetch counts in parallel
    const countPromises = stageIds.map(async (stageId) => {
      const count = await this.getStageCount(pipelineId, stageId, filters);
      return { stageId, count };
    });

    const results = await Promise.all(countPromises);
    
    results.forEach(({ stageId, count }) => {
      counts[stageId] = count;
    });

    return counts;
  }

  // Get aggregates (total value and kW) for a stage
  async getStageAggregates(pipelineId: number, stageId: number, filters: PipelineFilterOptions): Promise<StageAggregates> {
    try {
      const tenantId = await this.resolveAccount(filters);

      let query = supabase
        .from('projects')
        .select('id, system_cost, estimated_system_size, stage_changed_at, updated_at, created_at, is_retention')
        .eq('pipeline_id', pipelineId)
        .eq('current_stage_id', stageId)
        .eq('tenant_id', tenantId)
        .neq('project_status', 'Deleted')
        .is('deleted_at', null);

      query = this.applyFilters(query, filters);

      const { data, error } = await query;
      if (error) throw error;

      if (!data || data.length === 0) {
        return { totalValue: 0, totalKW: 0, slaAges: [] };
      }

      const slaAges: SlaAge[] = data.map((project) => ({
        id: project.id,
        stageChangedAt: project.stage_changed_at ?? project.updated_at ?? project.created_at ?? null,
        isRetention: !!project.is_retention,
      }));

      const totalValue = data.reduce((sum, project) => sum + (project.system_cost || 0), 0);
      const totalKW = data.reduce((sum, project) => {
        const size = project.estimated_system_size;
        if (typeof size === 'number') return sum + size;
        if (size !== null && size !== undefined) {
          const parsed = parseFloat(String(size));
          return sum + (isNaN(parsed) ? 0 : parsed);
        }
        return sum;
      }, 0);

      return { totalValue, totalKW, slaAges };
    } catch (error) {
      console.error('Error getting stage aggregates:', error);
      return { totalValue: 0, totalKW: 0 };
    }
  }

  // Client-side filtering for cached projects (much faster than server-side)
  private filterProjectsClientSide(projects: any[], filters: PipelineFilterOptions): any[] {
    // If no filters are applied, return all projects
    if (!filters.search && !filters.statusFilter && !filters.teamId && !filters.assignedRepId &&
        !filters.dateRange && !filters.stageChangeDateRange && !filters.hasOpenComments && !filters.systemSizeRange) {
      return projects;
    }

    const filtered = projects.filter(project => {
      // Search filter
      if (filters.search) {
        const searchTerm = filters.search.toLowerCase();
        const matchesSearch = (
          // Basic project fields
          (project.project_name?.toLowerCase() || '').includes(searchTerm) ||
          (project.company_name?.toLowerCase() || '').includes(searchTerm) ||
          (project.project_city?.toLowerCase() || '').includes(searchTerm) ||
          (project.project_state?.toLowerCase() || '').includes(searchTerm) ||
          (project.project_street?.toLowerCase() || '').includes(searchTerm) ||
          (project.first_name?.toLowerCase() || '').includes(searchTerm) ||
          (project.last_name?.toLowerCase() || '').includes(searchTerm) ||
          (project.contact_email?.toLowerCase() || '').includes(searchTerm) ||
          (project.contact_phone?.toLowerCase() || '').includes(searchTerm) ||
          (project.utility_provider?.toLowerCase() || '').includes(searchTerm) ||
          (project.project_full_address?.toLowerCase() || '').includes(searchTerm) ||
          (project.company_street?.toLowerCase() || '').includes(searchTerm) ||
          (project.company_city?.toLowerCase() || '').includes(searchTerm) ||
          (project.company_state?.toLowerCase() || '').includes(searchTerm) ||
          (project.property_owner_name?.toLowerCase() || '').includes(searchTerm) ||
          // Team and user fields (these are added after processing)
          (project.team_name?.toLowerCase() || '').includes(searchTerm) ||
          (project.salesRep?.name?.toLowerCase() || '').includes(searchTerm) ||
          (project.salesRep?.email?.toLowerCase() || '').includes(searchTerm) ||
          (project.projectManager?.name?.toLowerCase() || '').includes(searchTerm) ||
          (project.projectManager?.email?.toLowerCase() || '').includes(searchTerm)
        );
        if (!matchesSearch) return false;
      }

      // Status filter
      if (filters.statusFilter && project.project_status !== filters.statusFilter) {
        return false;
      }

      // Team filter - this is the important one!
      if (filters.teamId && project.team_id !== filters.teamId) {
        return false;
      }

      // Assigned rep filter
      if (filters.assignedRepId) {
        if (filters.assignedRepId === 'unassigned') {
          // Show only unassigned projects (where assigned_sales_rep_id is null/undefined)
          if (project.assigned_sales_rep_id) {
            return false;
          }
        } else {
          // Show only projects assigned to the specific rep
          if (project.assigned_sales_rep_id !== filters.assignedRepId) {
            return false;
          }
        }
      }

      // Date range filter
      if (filters.dateRange?.from && new Date(project.created_at) < filters.dateRange.from) {
        return false;
      }
      if (filters.dateRange?.to && new Date(project.created_at) > filters.dateRange.to) {
        return false;
      }

      // Stage change date range filter
      if (filters.stageChangeDateRange?.from && project.stage_changed_at && 
          new Date(project.stage_changed_at) < filters.stageChangeDateRange.from) {
        return false;
      }
      if (filters.stageChangeDateRange?.to && project.stage_changed_at && 
          new Date(project.stage_changed_at) > filters.stageChangeDateRange.to) {
        return false;
      }

      // System size filter
      if (filters.systemSizeRange) {
        const size = project.estimated_system_size || 0;
        if (size < filters.systemSizeRange[0] || size > filters.systemSizeRange[1]) {
          return false;
        }
      }

      return true;
    });

    return filtered;
  }

  // Process projects with user data (extracted from main method)
  private async processProjectsWithUserData(projects: any[]): Promise<any[]> {
    if (!projects || projects.length === 0) return [];

    // Get sales rep IDs for team lookups (even though user data is now in the query)
    const salesRepIds = projects.map(p => p.assigned_sales_rep_id).filter(Boolean);
    const pmIds = projects.map(p => p.assigned_internal_pm_id).filter(Boolean);
    // SUB-4.16. The designer is named by the SAME card and was broken the same
    // way. Leaving one of three assignee slots reading a uuid is the half-fix
    // somebody rediscovers in a month.
    const designerIds = projects.map(p => p.assigned_proposal_designer_id).filter(Boolean);

    // Fetch user data for sales reps and PMs if not already in the project
    const uniqueUserIds = [...new Set([...salesRepIds, ...pmIds, ...designerIds])];
    // SUB-4.16. `failed` travels with the rows so the card can say "Unavailable"
    // rather than inventing "Not Assigned" or printing an id.
    const { byId: userDataMap, failed: assigneeReadFailed } = uniqueUserIds.length > 0
      ? await this.getCachedUserData(uniqueUserIds)
      : { byId: {} as Record<string, any>, failed: false };

    // Fetch team relationships
    const teamRelationships = salesRepIds.length > 0
      ? await supabase
          .from('user_teams')
          .select('user_id, team_id')
          .in('user_id', salesRepIds)
      : { data: [] };

    // Get unique team IDs and fetch team names
    const teamIds = [...new Set(teamRelationships.data?.map(rel => rel.team_id).filter(Boolean) || [])];
    const { data: teamsData } = teamIds.length > 0
      ? await supabase
          .from('teams')
          .select('id, name')
          .in('id', teamIds)
      : { data: [] };

    const teamNameMap = new Map<number, string>();
    teamsData?.forEach(team => {
      teamNameMap.set(team.id, team.name);
    });

    // Create team lookup map
    const userTeamMap = new Map<string, number>();
    teamRelationships.data?.forEach(rel => {
      userTeamMap.set(rel.user_id, rel.team_id);
    });

    // Map projects with related data
    return projects.map(project => {
      // Calculate days in stage
      const stageUpdatedAt = project.stage_changed_at
        ? new Date(project.stage_changed_at)
        : (project.updated_at ? new Date(project.updated_at) : new Date(project.created_at));
      const now = new Date();
      const diffTime = Math.max(0, now.getTime() - stageUpdatedAt.getTime());
      const daysInStage = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

      // Calculate total days
      const createdAt = new Date(project.created_at);
      const totalDiffTime = Math.max(0, now.getTime() - createdAt.getTime());
      const totalDays = Math.ceil(totalDiffTime / (1000 * 60 * 60 * 24));

      // Get team ID from user-team mapping
      const teamId = project.assigned_sales_rep_id ? userTeamMap.get(project.assigned_sales_rep_id) || null : null;

      /**
       * SUB-4.16 · D67 applied to people. ONE source for a name.
       *
       * ⚠️ **THE `sales_rep` EMBED IS GONE FROM THE QUERIES, SO IT IS GONE FROM
       * HERE.** This used to prefer `project.sales_rep`, a PostgREST embed
       * bounded by single-tenant `users_select`, and fall back to a `users`
       * fetch bounded by the same policy. Both were blind to a sub-account's
       * staff, so both returned null in merged scope and the card printed a raw
       * uuid. Keeping the embed as a "fast path" would leave two readings of one
       * name, which is the shape SUB-5.5 refused.
       *
       * ⚠️ Names are split back into first/last ONLY because existing consumers
       * read those fields. `name` is `getUserDisplayName`'s answer and is the
       * one to trust; the split is lossy for a suffixed or single-token name.
       */
      const fromDirectory = (userId: string | null | undefined) => {
        if (!userId) return null;
        const hit = userDataMap[userId];
        if (!hit) return null;
        return {
          name: hit.name,
          email: hit.email,
          first_name: hit.record?.first_name ?? hit.name.split(' ')[0] ?? '',
          last_name: hit.record?.last_name ?? hit.name.split(' ').slice(1).join(' ') ?? '',
        };
      };

      const salesRep = fromDirectory(project.assigned_sales_rep_id);

      const projectManager = fromDirectory(project.assigned_internal_pm_id);
      const proposalDesigner = fromDirectory(project.assigned_proposal_designer_id);

      return {
        ...project,
        name: project.project_name || '',
        company: project.company_name || '',
        value: project.system_cost || 0,
        total_project_profit: project.total_project_profit || 0,
        daysInStage,
        totalDays,
        assignedToId: project.assigned_sales_rep_id,
        city: project.project_city,
        state: project.project_state,
        comments: [], // Load on demand
        project_messages: [], // Load on demand
        salesRep,
        projectManager,
        /**
         * SUB-4.16. The RAW rows, so the card can tell the three states apart.
         * `salesRep` above stays exactly as it was because the board's search
         * and sort read `salesRep.name`; these are additive.
         */
        proposal_designer: proposalDesigner,
        salesRepRecord: userDataMap[project.assigned_sales_rep_id]?.record ?? null,
        projectManagerRecord: userDataMap[project.assigned_internal_pm_id]?.record ?? null,
        proposalDesignerRecord: userDataMap[project.assigned_proposal_designer_id]?.record ?? null,
        assignedPmId: project.assigned_internal_pm_id ?? null,
        assignedDesignerId: project.assigned_proposal_designer_id ?? null,
        /** ⚠️ True when the NAME LOOKUP failed, never when a slot is empty. */
        assigneeReadFailed,
        team_id: teamId,
        team_name: teamId ? teamNameMap.get(teamId) || '' : ''
      };
    });
  }

  // Fetch paginated projects for a stage
  async getStageProjects(
    pipelineId: number,
    stageId: number,
    offset: number = 0,
    limit: number = 10,
    filters: PipelineFilterOptions
  ) {
    try {
      const tenantId = await this.resolveAccount(filters);

      // For team filtering, hasOpenComments, or SEARCH, we need client-side filtering
      // since these require joins that the optimized function doesn't handle
      // Search needs client-side filtering to search sales_rep and pm names from joined data
      if (filters.teamId || filters.hasOpenComments || filters.search) {
        const allProjects = await this.getAllStageProjects(pipelineId, stageId, filters);

        // IMPORTANT: Process projects with user data FIRST, so we have team names and sales rep data for filtering
        const processedProjects = await this.processProjectsWithUserData(allProjects);

        // THEN filter the processed projects (which now include team names, sales rep, pm)
        const filteredProjects = this.filterProjectsClientSide(processedProjects, filters);

        // Apply pagination and return
        const paginatedResults = filteredProjects.slice(offset, offset + limit);
        return paginatedResults;
      }

      // Use optimized database function for other filters (not search)
      // This is MUCH faster than fetching all and filtering client-side
      if (filters.statusFilter || filters.assignedRepId ||
          filters.dateRange || filters.stageChangeDateRange || filters.systemSizeRange) {

        const { data: projectsData, error: projectsError } = await supabase.rpc('search_projects_optimized', {
          p_pipeline_id: pipelineId,
          p_stage_id: stageId,
          p_tenant_id: tenantId,
          p_search_term: filters.search || null,
          p_status_filter: filters.statusFilter || null,
          p_assigned_rep_id: filters.assignedRepId || null,
          p_date_from: filters.dateRange?.from?.toISOString() || null,
          p_date_to: filters.dateRange?.to?.toISOString() || null,
          p_stage_date_from: filters.stageChangeDateRange?.from?.toISOString() || null,
          p_stage_date_to: filters.stageChangeDateRange?.to?.toISOString() || null,
          p_system_size_min: filters.systemSizeRange?.[0] || null,
          p_system_size_max: filters.systemSizeRange?.[1] || null,
          p_limit: limit,
          p_offset: offset
        });

        if (projectsError) throw projectsError;

        return this.processProjectsWithUserData(projectsData || []);
      }

      // For non-filtered queries, use simple server-side pagination
      const query = supabase
        .from('projects')
        .select(`
          id,
          project_name,
          company_name,
          system_cost,
          total_project_profit,
          estimated_system_size,
          project_city,
          project_state,
          assigned_sales_rep_id,
          assigned_internal_pm_id,
          current_stage_id,
          created_at,
          updated_at,
          stage_changed_at,
          project_status,
          project_type,
          commercial_type,
          pipeline_id,
          tenant_id,
          created_by_user,
          first_name,
          last_name,
          contact_email,
          contact_phone,
          utility_provider,
          project_full_address,
          company_street,
          company_city,
          company_state,
          property_owner_name,
          is_retention,
          assigned_proposal_designer_id
        `)
        .eq('pipeline_id', pipelineId)
        .eq('current_stage_id', stageId)
        .eq('tenant_id', tenantId)
        .neq('project_status', 'Deleted')
        .is('deleted_at', null)
        .range(offset, offset + limit - 1)
        .order('created_at', { ascending: false });

      const { data: projectsData, error: projectsError } = await query;
      if (projectsError) throw projectsError;

      return this.processProjectsWithUserData(projectsData || []);
    } catch (error) {
      console.error('Error fetching stage projects:', error);
      return [];
    }
  }

  // Batch method to initialize all stage data efficiently
  async initializePipelineData(pipelineId: number, stageIds: number[], filters: PipelineFilterOptions) {
    
    const tenantId = await this.resolveAccount(filters);
    
    // Create all queries in parallel
    const queries = stageIds.map(stageId => ({
      stageId,
      countQuery: this.getStageCount(pipelineId, stageId, filters),
      projectsQuery: this.getStageProjects(pipelineId, stageId, 0, 20, filters) // Optimized limit for faster loading
    }));

    // Execute all queries in parallel
    const results = await Promise.allSettled(
      queries.map(async ({ stageId, countQuery, projectsQuery }) => ({
        stageId,
        count: await countQuery,
        projects: await projectsQuery
      }))
    );

    // Process results
    const stageCounts: Record<number, number> = {};
    const stageProjects: Record<number, any[]> = {};
    const loadedCounts: Record<number, number> = {};

    results.forEach((result, index) => {
      const stageId = stageIds[index];
      if (result.status === 'fulfilled') {
        stageCounts[stageId] = result.value.count;
        stageProjects[stageId] = result.value.projects;
        loadedCounts[stageId] = result.value.projects.length;
      } else {
        console.error(`Failed to load stage ${stageId}:`, result.reason);
        stageCounts[stageId] = 0;
        stageProjects[stageId] = [];
        loadedCounts[stageId] = 0;
      }
    });


    return {
      stageCounts,
      stageProjects,
      loadedCounts
    };
  }

  // Fetch ALL projects for a pipeline (used by list view - no pagination)
  async getAllPipelineProjects(pipelineId: number, filters: PipelineFilterOptions): Promise<any[]> {
    try {
      const tenantId = await this.resolveAccount(filters);

      // Build base query
      let query = supabase
        .from('projects')
        .select(`
          id,
          project_name,
          company_name,
          system_cost,
          total_project_profit,
          estimated_system_size,
          project_city,
          project_state,
          assigned_sales_rep_id,
          assigned_internal_pm_id,
          current_stage_id,
          created_at,
          updated_at,
          stage_changed_at,
          project_status,
          project_type,
          commercial_type,
          pipeline_id,
          tenant_id,
          created_by_user,
          first_name,
          last_name,
          contact_email,
          contact_phone,
          utility_provider,
          project_full_address,
          company_street,
          company_city,
          company_state,
          property_owner_name,
          project_street,
          is_retention,
          assigned_proposal_designer_id
        `)
        .eq('pipeline_id', pipelineId)
        .eq('tenant_id', tenantId)
        .neq('project_status', 'Deleted')
        .is('deleted_at', null)
        .order('created_at', { ascending: false });

      // Apply server-side filters where possible
      if (filters.statusFilter) {
        query = query.ilike('project_status', filters.statusFilter);
      }
      if (filters.assignedRepId) {
        query = query.eq('assigned_sales_rep_id', filters.assignedRepId);
      }
      if (filters.dateRange?.from) {
        query = query.gte('created_at', filters.dateRange.from.toISOString());
      }
      if (filters.dateRange?.to) {
        query = query.lte('created_at', filters.dateRange.to.toISOString());
      }
      if (filters.stageChangeDateRange?.from) {
        query = query.gte('stage_changed_at', filters.stageChangeDateRange.from.toISOString());
      }
      if (filters.stageChangeDateRange?.to) {
        query = query.lte('stage_changed_at', filters.stageChangeDateRange.to.toISOString());
      }
      if (filters.systemSizeRange) {
        query = query.gte('estimated_system_size', filters.systemSizeRange[0])
                     .lte('estimated_system_size', filters.systemSizeRange[1]);
      }

      const { data: projectsData, error: projectsError } = await query;
      if (projectsError) throw projectsError;

      // Process with user data
      let processedProjects = await this.processProjectsWithUserData(projectsData || []);

      // Apply client-side filters that require joined data
      if (filters.teamId || filters.hasOpenComments || filters.search) {
        processedProjects = this.filterProjectsClientSide(processedProjects, filters);
      }

      return processedProjects;
    } catch (error) {
      console.error('Error fetching all pipeline projects:', error);
      return [];
    }
  }
}

export const pipelinePaginationService = new PipelinePaginationService();